<template>
  <div class="tw:!w-full tw:!flex tw:!justify-center tw:!items-center">
    <div class="tw:!relative tw:!w-full tw:!max-w-lg tw:!font-sans">
      <Transition name="error-fade">
        <p v-if="hasError" class="tw:!text-xs tw:!text-red-500 tw:!mb-2 tw:!text-center tw:!flex tw:!items-center tw:!justify-center tw:!gap-1">
          {{ $t('search.validation.selectCountryError') }}
        </p>
      </Transition>

      <div
        class="liquid-glass-input tw:!flex tw:!items-center tw:!gap-2 tw:!px-4 tw:!py-3 tw:!rounded-full tw:!transition-all tw:!duration-300"
        :class="[isOpen ? 'input-focused' : '', hasError ? 'input-error' : '']"
      >
        <Search class="tw:!w-5 tw:!h-5 tw:!text-gray-400 tw:!flex-shrink-0" />
        <span class="tw:!w-px tw:!h-5 tw:!bg-gray-200 tw:!flex-shrink-0" />
        <input
          id="search-esim"
          ref="inputRef"
          v-model="query"
          type="text"
          :placeholder="isOpen ? '' : $t('search.placeholder')"
          class="tw:!flex-1 tw:!bg-transparent tw:!border-none tw:!outline-none tw:!text-gray-800 tw:!text-base tw:!font-semibold tw:!placeholder-gray-800 tw:!caret-gray-800"
          @focus="handleFocus"
          @blur="handleBlur"
          @keydown="handleKeydown"
        />
        <button
          v-if="query"
          class="tw:!flex-shrink-0 tw:!text-gray-400 hover:tw:!text-gray-600 tw:!transition-colors"
          @mousedown.prevent="clearQuery"
        >
          <XCircle class="tw:!w-5 tw:!h-5" />
        </button>
      </div>

      <Transition name="dropdown">
        <div
          v-if="isOpen && results.length"
          class="liquid-glass-dropdown tw:!absolute tw:!top-full tw:!left-0 tw:!right-0 tw:!mt-2 tw:!rounded-2xl tw:!overflow-hidden tw:!z-[9999]"
        >
          <div class="tw:!flex tw:!items-center tw:!justify-between tw:!px-4 tw:!py-3 tw:!border-b tw:!border-white/10">
            <span class="tw:!text-xs tw:!font-bold tw:!tracking-widest tw:!text-gray-900 tw:!uppercase">
              {{ $t('search.destinations') }}
            </span>
            <span class="tw:!text-xs tw:!font-semibold tw:!text-gray-900/40 tw:!uppercase">
              {{ results.length }} {{ results.length !== 1 ? $t('search.resultsPlural') : $t('search.results') }}
            </span>
          </div>

          <ul v-if="results.length > 0">
            <li
              v-for="(item, index) in results"
              :key="item.slug"
              class="tw:!flex tw:!items-center tw:!justify-between tw:!px-4 tw:!py-3 tw:!cursor-pointer tw:!transition-all tw:!duration-150 tw:!border-b tw:!border-white/5 last:tw:!border-b-0"
              :class="autocomplete.counter.value === index ? 'list-item-active' : 'list-item-default'"
              @mousedown.prevent="selectItem(item)"
            >
              <div class="tw:!flex tw:!items-center tw:!gap-3">
                <div class="tw:!flex-shrink-0">
                  <img
                    v-if="item.type === 1"
                    :src="item.flag"
                    :alt="item.name"
                    class="tw:!block tw:!w-8 tw:!h-auto tw:!rounded-md tw:!border tw:!border-gray-200 tw:!object-contain"
                  />
                  <Globe v-else class="tw:!w-5 tw:!h-5 tw:!text-gray-800/70" />
                </div>
                <span class="tw:!text-sm tw:!font-medium tw:!text-gray-800 tw:!truncate tw:!max-w-[200px]">
                  {{ item.name }}
                </span>
              </div>

              <span class="type-badge tw:!text-xs tw:!font-medium tw:!px-3 tw:!py-1 tw:!rounded-full tw:!flex-shrink-0">
                {{ item.type === 1 ? $t('search.country') : $t('search.region') }}
              </span>
            </li>
          </ul>

          <div v-else class="tw:!flex tw:!flex-col tw:!items-center tw:!justify-center tw:!py-8 tw:!gap-2 tw:!text-gray/40">
            <SearchIcon class="tw:!w-6 tw:!h-6" />
            <span class="tw:!text-sm">{{ $t('search.noResults') }}</span>
          </div>
        </div>
      </Transition>

      <div class="tw:!mt-3 tw:!flex tw:!flex-col tw:!gap-1">
        <button
          class="find-plan-btn tw:!flex tw:!items-center tw:!py-2 tw:!rounded-full tw:!text-sm tw:!font-bold tw:!transition-all tw:!duration-200 tw:!flex-1 tw:!justify-center"
          @click="handleFindPlan"
        >
          {{ $t('search.esimPlan') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Search, XCircle, Search as SearchIcon, Globe } from 'lucide-vue-next'
import CountriesData from '~/assets/countriesData.json'
import { getFlag } from '~/utility/helper'
import { useAutocompletionHelper } from '~/composables/useAutocompletionHelper'

type SearchItem = { name: string; slug: string; type: number; flag: string }

const emit = defineEmits<{
  'select-country': [slug: string, name: string]
}>()

const query = ref('')
const isOpen = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)
const selectedItem = ref<SearchItem | null>(null)
const hasError = ref(false)

const PRIORITY_CODES = ['MC', 'IN', 'JP', 'ID', 'BD', 'PK', 'NG', 'GH', 'US', 'CN']

const allItems = computed(() =>
  CountriesData.map(item => ({
    name: item.name,
    slug: item.code,
    type: item.type,
    flag: getFlag(item.code),
  })).sort((a, b) =>
    +PRIORITY_CODES.includes(b.slug) - +PRIORITY_CODES.includes(a.slug)
  )
)

const results = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return allItems.value.slice(0, 5)

  return allItems.value
    .filter(item => item.name.toLowerCase().includes(q))
    .slice(0, 5)
})

const autocomplete = useAutocompletionHelper(results, query, selectItem)

function selectItem(item: SearchItem) {
  query.value = item.name
  selectedItem.value = item
  hasError.value = false
  isOpen.value = false
  inputRef.value?.blur()
}

function handleFindPlan() {
  if (!selectedItem.value) {
    hasError.value = true
    return
  }
  hasError.value = false
  emit('select-country', selectedItem.value.slug, selectedItem.value.name)
}

function handleKeydown(e: KeyboardEvent) {
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      autocomplete.down()
      break
    case 'ArrowUp':
      e.preventDefault()
      autocomplete.up()
      break
    case 'Enter':
      e.preventDefault()
      autocomplete.enter()
      break
    case 'Escape':
      isOpen.value = false
      break
  }
}

function clearQuery() {
  query.value = ''
  selectedItem.value = null
  hasError.value = false
  isOpen.value = true
  inputRef.value?.focus()
}

function handleBlur() {
  setTimeout(() => { isOpen.value = false }, 150)
}

function handleFocus() {
  isOpen.value = true
  hasError.value = false
}
</script>

<style scoped>
.liquid-glass-input {
  background: rgba(255, 255, 255, 1);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.10),
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.03);
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: box-shadow;
  isolation: isolate;
}

.liquid-glass-input.input-focused {
  background: rgba(255, 255, 255, 1);
  border-color: rgba(0, 0, 0, 0.25);
  box-shadow:
    0 4px 32px rgba(0, 0, 0, 0.14),
    0 0 0 3px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.04);
}

.liquid-glass-input.input-error {
  border-color: rgba(239, 68, 68, 0.7);
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.10),
    0 0 0 3px rgba(239, 68, 68, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.03);
}

.liquid-glass-input.input-error.input-focused {
  border-color: rgba(239, 68, 68, 0.9);
  box-shadow:
    0 4px 32px rgba(239, 68, 68, 0.18),
    0 0 0 3px rgba(239, 68, 68, 0.20),
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.04);
}

.liquid-glass-dropdown {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: 1px solid rgba(255, 255, 255, 0.20);
  box-shadow:
    0 8px 40px rgba(0, 0, 0, 0.20),
    0 2px 8px rgba(0, 0, 0, 0.10);
}

.liquid-glass-dropdown .tw\:text-white\/60,
.liquid-glass-dropdown .tw\:text-white\/40 {
  color: rgba(0, 0, 0, 0.5) !important;
}

.liquid-glass-dropdown li span.tw\:text-white {
  color: rgba(0, 0, 0, 0.85) !important;
}

.list-item-default:hover {
  background: rgba(0, 0, 0, 0.05);
}

.list-item-active {
  background: rgba(0, 0, 0, 0.08);
}

.type-badge {
  background: rgba(0, 0, 0, 0.07);
  color: rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(0, 0, 0, 0.10);
}

.find-plan-btn {
  background: var(--esim-primary);
  color: var(--esim-primary-text);
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 2px 12px var(--esim-primary-glow),
    0 0 0 1px var(--esim-primary-glow);
  cursor: pointer;
}

.find-plan-btn:hover:not(:disabled) {
  background: var(--esim-primary-hover);
  box-shadow:
    0 4px 20px var(--esim-primary-glow-hover),
    0 0 0 2px var(--esim-primary-glow);
  transform: translateY(-1px);
}

.find-plan-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  background: var(--esim-primary);
  box-shadow: 0 2px 8px var(--esim-primary-glow);
}

@media (max-width: 768px) {
  .liquid-glass-input {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
}
</style>